import { useState } from "react";

import { cx } from "@/utils/cx";

import type { NavItemDividerType, NavItemType } from "../config";

import { NavItemBase } from "./nav-item";

interface NavListProps {
	/** URL of the currently active item. */
	activeUrl?: string;
	/** Additional CSS classes to apply to the list. */
	className?: string;
	/** List of items to display. */
	items: (NavItemDividerType | NavItemType)[];
}

const isUrlActive = (
	href: string | undefined,
	activeUrl: string | undefined,
): boolean => {
	if (!href || !activeUrl) return false;
	// Exact match
	if (href === activeUrl) return true;
	// Check if activeUrl starts with href and the next character is '/' or end of string
	// This allows sub-routes like /dashboard/event/123 to match /dashboard
	return (
		activeUrl.startsWith(href) &&
		(activeUrl.length === href.length || activeUrl[href.length] === "/")
	);
};

export const NavList = ({ activeUrl, className, items }: NavListProps) => {
	const [open, setOpen] = useState(false);

	return (
		<ul className={cx("mt-4 flex flex-col px-4", className)}>
			{items.map((item, index) => {
				if (item.hidden) return null;
				if (item.divider) {
					return (
						<li className="w-full px-0.5 py-2" key={index}>
							<hr className="h-px w-full border-none bg-border-secondary_alt" />
						</li>
					);
				}

				if (item.items?.length) {
					return (
						<details
							className="appearance-none py-0.5"
							key={item.label}
							onToggle={(e) => {
								setOpen(e.currentTarget.open);
							}}
							open={isUrlActive(item.href, activeUrl)}
						>
							<NavItemBase
								badge={item.badge}
								href={item.href}
								icon={item.icon}
								testId={item.testId}
								type="collapsible"
							>
								{item.label}
							</NavItemBase>

							<dd>
								<ul className="py-0.5">
									{item.items.map((childItem) => (
										<li className="py-0.5" key={childItem.label}>
											<NavItemBase
												badge={childItem.badge}
												current={isUrlActive(childItem.href, activeUrl)}
												href={childItem.href}
												testId={childItem.testId}
												type="collapsible-child"
											>
												{childItem.label}
											</NavItemBase>
										</li>
									))}
								</ul>
							</dd>
						</details>
					);
				}

				return (
					<li className="py-0.5" key={item.label}>
						<NavItemBase
							badge={item.badge}
							current={isUrlActive(item.href, activeUrl)}
							href={item.href}
							icon={item.icon}
							open={open && isUrlActive(item.href, activeUrl)}
							testId={item.testId}
							type="link"
						>
							{item.label}
						</NavItemBase>
					</li>
				);
			})}
		</ul>
	);
};
